// Content adapter — Phase 5 stale-while-revalidate layer.
//
// Each hook here exposes one editorial domain (siteSettings, heroMedia,
// galleries, prints, testimonials, journal posts).
//
// Pattern (uniform across every hook):
//   1. Initial render returns the `data.js` fallback synchronously.
//      No skeleton, no empty state — the user sees real content on first
//      paint.
//   2. A background Sanity fetch is fired in `useEffect` with a 3s
//      timeout (see SANITY_FETCH_TIMEOUT_MS in sanityClient.js).
//   3. On success, state is updated with the live Sanity value. The
//      component re-renders. Because Sanity is shape-equivalent to data.js
//      (verified by Phase 4 migration), there is no UI flash.
//   4. On error or timeout, the fallback stays. A debug log is emitted to
//      console (no user-facing error).
//
// Rollback: every hook here can be swapped back to a direct `data.js`
// import in a single line. The content adapter is the seam between the
// FE and Sanity; FE components only touch hooks from this file.
//
// What this file does NOT cover: static enums + copy that Phase 4 left
// in `data.js` permanently (BUSINESS_HERO, VIDEO_REELS, SCOPE_CATEGORIES,
// MENTORING_OFFERS, packages, etc — see SPEC.md §3 + Phase 4 §5 sign-off).
// Components that read those continue to import from `@/lib/data` directly.

import {useEffect, useState} from 'react';
import {fetchWithFallback} from './sanityClient';
import {
    SITE as FALLBACK_SITE,
    REVIEWS as FALLBACK_REVIEWS,
    PRINTS as FALLBACK_PRINTS,
    TRAVEL_ARCHIVE as FALLBACK_TRAVEL_ARCHIVE,
    PORTFOLIO as FALLBACK_PORTFOLIO,
    BUSINESS_GALLERY_ITEMS as FALLBACK_BUSINESS_GALLERY,
    HOME_SERVICES_GALLERY_ITEMS as FALLBACK_HOME_SERVICES_GALLERY,
    HERO_IMAGE as FALLBACK_HERO_IMAGE,
    HERO_IMAGE_MOBILE as FALLBACK_HERO_IMAGE_MOBILE,
    HERO_IMAGE_SRCSET as FALLBACK_HERO_IMAGE_SRCSET,
    ABOUT_IMAGE as FALLBACK_ABOUT_IMAGE,
} from './data';

// Cloudinary URL builder — mirrors the `cld()` helper in data.js so that
// Sanity-sourced images receive the same `f_auto,q_auto,w_…` transforms
// as the data.js fallback values. Without this, the live-data swap would
// regress image quality (synthetic secure_url has no transform segment).
const CLOUDINARY_CLOUD = 'dub8rhyez';
const cldUrl = (publicId, transform = 'f_auto,q_auto,w_1600') =>
    publicId
        ? `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/${transform}/${publicId}`
        : null;

// Phase 14 — video URL helpers. Mirrors `cldUrl` but targets Cloudinary's
// /video/upload/ resource path. The `cldPosterUrl` helper extracts the
// frame at offset 0 (`so_0`) of the source video and serves it as an
// auto-format still image, which the FE uses as the <video poster> +
// the in-grid blur-up placeholder so video tiles never flash blank.
const cldVideoUrl = (publicId, transform = 'f_auto,q_auto,vc_auto') =>
    publicId
        ? `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/video/upload/${transform}/${publicId}`
        : null;

const cldPosterUrl = (publicId, transform = 'so_0,f_auto,q_auto,w_1600,c_limit') =>
    publicId
        ? `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/video/upload/${transform}/${publicId}.jpg`
        : null;

// gallery item (Sanity cloudinaryAsset inline) → PORTFOLIO item (FE shape).
// Required keys (because Portfolio.jsx + LifeLegacy.jsx + BrandBusiness.jsx
// + MediaGallery consume these): id, publicId, title, alt, caption,
// category (Title Case), lifeTags, brandTags, image, imageHome, srcSet,
// ratio, featured, location.
//
// Category translation: cloudinaryAsset.categories is stored as lowercase
// strings ('portraits', 'family', 'branding', …). The FE filters on Title
// Case 'Portraits' / 'Family' / 'Branding'. Map at the seam.
const FE_CATEGORIES_FROM_LOWER = {
    portraits: 'Portraits',
    maternity: 'Maternity',
    family: 'Family',
    branding: 'Branding',
    events: 'Events',
    business: 'Business',
    editorial: 'Editorial',
};
const FE_CANONICAL_CATEGORIES = new Set(Object.values(FE_CATEGORIES_FROM_LOWER));

const inferFECategory = (categoriesLower) => {
    if (!Array.isArray(categoriesLower)) return null;
    for (const c of categoriesLower) {
        const titleCase = FE_CATEGORIES_FROM_LOWER[c];
        if (titleCase && FE_CANONICAL_CATEGORIES.has(titleCase)) return titleCase;
    }
    return null;
};

const mapGalleryItemToFE = (item) => {
    if (!item) return null;
    const publicId = item.image?.public_id;
    if (!publicId) return null;

    // Phase 14 — detect video items by Cloudinary's `resource_type`. The
    // Sanity Cloudinary plugin populates this on every asset. Anything that
    // isn't an explicit "video" is treated as an image (defensive default —
    // preserves existing behavior for the 99% case where galleries only
    // hold photos).
    const isVideo = item.image?.resource_type === 'video';

    // Phase 15 — explicit aspectRatio overrides intrinsic dims. Strings
    // map to ratio numbers; anything unrecognised (including empty/null)
    // falls through to the intrinsic-dim path below.
    const ASPECT_MAP = {
        '16:9': 16 / 9,
        '9:16': 9 / 16,
        '1:1': 1,
        '4:5': 4 / 5,
    };
    const explicitRatio = ASPECT_MAP[item.aspectRatio] || null;

    // Aspect ratio from the Cloudinary asset's intrinsic dimensions when
    // available. The MediaGallery already has a fallback (DEFAULT_RATIO =
    // 0.8 = 4:5 portrait) for items without `ratio`, but for video tiles
    // we *prefer* a real ratio so a landscape clip doesn't get rendered
    // in a portrait grid box (which would crop heavily). Image items can
    // continue to lean on MediaGallery's per-consumer defaults.
    const naturalRatio =
        item.image?.width && item.image?.height
            ? item.image.width / item.image.height
            : null;

    const common = {
        id: item._key || publicId,
        publicId,
        title: item.caption || item.alt || '',
        alt: item.alt || item.caption || 'Photograph by Elizaveta von Stuben Imagery',
        caption: item.caption || '',
        category: inferFECategory(item.categories),
        lifeTags: Array.isArray(item.lifeTags) ? item.lifeTags : [],
        brandTags: Array.isArray(item.brandTags) ? item.brandTags : [],
        featured: !!item.featured,
        location: item.location || null,
    };

    if (isVideo) {
        // Video item — emit videoSrc + posterSrc + a "spirit" image URL
        // pointing at the same poster so any image-only consumer (e.g. an
        // unmigrated home-page strip) still renders something sensible
        // until it's upgraded to handle `mediaType: 'video'`.
        //
        // Phase 15 — prefer the editor's explicit `poster` field when set
        // (an image picked from Cloudinary in Studio), else fall back to
        // Cloudinary's so_0 frame-extraction from the source video. This
        // guarantees video tiles never render blank or broken.
        const posterPublicId = item.poster?.public_id;
        const poster = posterPublicId
            ? cldUrl(posterPublicId, 'f_auto,q_auto,w_1600')
            : cldPosterUrl(publicId);
        const posterHome = posterPublicId
            ? cldUrl(posterPublicId, 'f_auto,q_auto,w_900')
            : cldPosterUrl(publicId, 'so_0,f_auto,q_auto,w_900,c_limit');
        return {
            ...common,
            mediaType: 'video',
            videoSrc: cldVideoUrl(publicId),
            posterSrc: poster,
            // Image-shape compatibility: any existing consumer that reads
            // `.image` / `.imageHome` / `.srcSet` gets the poster still
            // rather than a broken/missing URL.
            image: poster,
            imageHome: posterHome,
            srcSet: null,
            ratio: explicitRatio || naturalRatio,
            // Pass intrinsic dimensions so MediaGallery's PhotoSwipe
            // dataSource builder can fall back to them when no probe ran
            // (videos don't fire the existing <img>.naturalWidth probe).
            naturalWidth: item.image?.width || null,
            naturalHeight: item.image?.height || null,
            duration: item.image?.duration || null,
        };
    }

    // Default — image item, same shape as pre-Phase-14, with optional
    // Phase 15 explicit aspectRatio override.
    return {
        ...common,
        mediaType: 'image',
        // Image URL variants — match the data.js PORTFOLIO item shape so
        // MediaGallery and the masonry layout can read them directly.
        image: cldUrl(publicId, 'f_auto,q_auto,w_1600'),
        imageHome: cldUrl(publicId, 'f_auto,q_auto,w_900'),
        srcSet: [400, 700, 1000, 1400, 1800, 2400]
            .map((w) => `${cldUrl(publicId, `f_auto,q_auto,w_${w}`)} ${w}w`)
            .join(', '),
        // Phase 15 — only emit `ratio` when the editor set an explicit
        // aspectRatio. Otherwise leave undefined so each consumer page's
        // ratio strategy still applies (Portfolio/LifeLegacy/BrandBusiness
        // compute their own per-item ratio from the image dims; the
        // BusinessArizona / AestheticClinic direct-passthrough pages
        // inherit MediaGallery's DEFAULT_RATIO=0.8 fallback). See the
        // Round 5.4 regression note: CSS `aspect-ratio` rejects the
        // 'W:H' colon syntax silently — passing the wrong string would
        // collapse tiles to 0×0.
        ...(explicitRatio ? {ratio: explicitRatio} : {}),
    };
};

// Module-level gallery query cache. Both /life-legacy and /brand-business
// call useGallery('portfolio-all') — without dedup that's 2× network round
// trips on a fresh page load. The cache stores the in-flight Promise (so
// concurrent calls share it) and the resolved value (so re-mounts during
// the same session are free). Per-route navigation does NOT bust the
// cache; Phase 5 doesn't need real-time editorial updates.
const _galleryCache = new Map();

const fetchGalleryBySlug = (slug) => {
    if (_galleryCache.has(slug)) return _galleryCache.get(slug);
    // Phase 14 + 15: explicit `image` projection captures the Cloudinary
    // asset fields we need to detect and render video items —
    //   resource_type === 'video'  → render <video> in grid + lightbox
    //   width / height             → preserve natural aspect ratio
    //   duration                   → reserved for future caption use
    // Phase 15 adds two optional item-level fields:
    //   poster.public_id           → explicit video-poster override
    //                                (falls back to so_0 auto-derive)
    //   aspectRatio                → '16:9' | '9:16' | '1:1' | '4:5'
    //                                forced layout slot; falls back to
    //                                intrinsic asset dimensions
    // Image items keep working unchanged (resource_type === 'image' or
    // undefined falls through the default image path).
    const p = fetchWithFallback(
        `*[_type == "gallery" && slug.current == $slug && published == true][0].items[] {
            _key, alt, caption, featured, location, lifeTags, brandTags, categories,
            aspectRatio,
            image {
                public_id, resource_type, format, secure_url, width, height, duration
            },
            poster {
                public_id, resource_type, format, secure_url
            }
        }`,
        {slug},
        null,
    );
    _galleryCache.set(slug, p);
    // If the fetch resolves to null (timeout / not-found / error), evict
    // so a subsequent useGallery() call can retry rather than serving the
    // stale failure.
    p.then((value) => {
        if (value === null || value === undefined) _galleryCache.delete(slug);
    });
    return p;
};

// ─────────────────────────────────────────────────────────────────────────
// Mapping helpers — Sanity (camelCase) → FE-side shape
// ─────────────────────────────────────────────────────────────────────────

// siteSettings → SITE constant shape. The FE has historically read
// `SITE.name`, `SITE.short`, `SITE.instagram`, etc.; the Sanity doc uses
// `brandName`, `nameShort`, `instagramHandle`, etc. (see SPEC.md §5).
// This mapper is the single conversion point.
const mapSiteSettingsToSite = (doc) => {
    if (!doc) return null;
    return {
        name: doc.brandName,
        short: doc.nameShort,
        wordmark: doc.wordmark,
        cityShort: doc.cityShort,
        cityLong: doc.cityLong,
        email: doc.contactEmail,
        phone: doc.phone || '',
        instagram: doc.instagramHandle,
        instagramUrl: doc.instagramUrl,
        heroHeadline: doc.heroHeadline,
        heroSubhead: doc.heroSubhead,
        brandDescription: doc.brandDescription,
        brandDescriptionShort: doc.brandDescriptionShort,
        brandDescriptionMicro: doc.brandDescriptionMicro,
        tagline: doc.tagline,
        house: doc.house,
        availabilityNote: doc.availabilityNote,
        trustLine: doc.trustLine,
    };
};

// testimonial (Sanity) → REVIEWS item (FE shape).
// Sanity fields: _id, quote, attribution, sessionType, context, tags[], order,
//                published, featured (Phase 10), backdropImage (Phase 9).
// FE shape:     id, quote, name, type, context, featured, backdropImage, backdropAlt.
const mapTestimonialToFE = (doc) => {
    if (!doc) return null;
    // Phase 9 — backdrop image is the optional behind-quote photo on
    // /reviews and /business cards. Empty until user uploads in Studio.
    // Phase 10 — only surfaces when `featured === true` (paired flag);
    // mapper exposes both so the component can apply the gating.
    // Phase 11 — `context` is an optional 4th info line below sessionType.
    // Renders as small muted type, hidden cleanly when empty.
    const backdropPub = doc.backdropImage?.image?.public_id;
    return {
        id: String(doc._id || '').replace(/^testimonial-/, '') || doc._id,
        quote: doc.quote,
        name: doc.attribution,
        type: doc.sessionType,
        context: doc.context || null,
        featured: !!doc.featured,
        backdropImage: backdropPub
            ? cldUrl(backdropPub, 'f_auto,q_auto,w_1200,c_fill,g_auto')
            : null,
        backdropAlt: backdropPub
            ? (doc.backdropImage?.alt || `${doc.attribution} session`)
            : null,
    };
};

// print (Sanity) → PRINTS item (FE shape).
// Reconstructs the legacy display strings:
//   - edition:    "Edition of 12" | "Open Edition"
//   - collection: "<series> · Limited Edition" | "<series> · Open Edition"
//   - startingAt: "$650"
// from the camelCase Sanity fields (editionType, editionSize, series, startingPrice).
//
// Phase 7 (/atelier polish) additions:
//   - `series` (string | null) — top-level field for series grouping on
//     /atelier. Sourced from the Sanity `series` field. For data.js fallback
//     prints (which only carry the embedded `collection` string), the
//     grouping layer parses it out via `seriesFromCollection()` below.
//   - `order` (number | null) — stable within-series sort key. Sourced
//     from Sanity. Absent in data.js fallback; consumers fall back to
//     title alphabetical when missing.
const mapPrintToFE = (doc) => {
    if (!doc) return null;
    const editionLabel = doc.editionType === 'open'
        ? 'Open Edition'
        : (doc.editionSize ? `Edition of ${doc.editionSize}` : 'Limited Edition');
    const editionSuffix = doc.editionType === 'open' ? '· Open Edition' : '· Limited Edition';
    const publicId = doc.coverImage?.image?.public_id;
    // Phase 8 — `roomPreviewImage` is the optional interior-mockup shot
    // used by the "In Home" section on /atelier. Returns null when the
    // user hasn't uploaded one (which is the default).
    const roomPub = doc.roomPreviewImage?.image?.public_id;
    return {
        id: String(doc._id || '').replace(/^print-/, '') || doc._id,
        title: doc.title,
        edition: editionLabel,
        collection: doc.series ? `${doc.series} ${editionSuffix}` : editionLabel,
        series: doc.series || null,
        order: typeof doc.order === 'number' ? doc.order : null,
        statement: doc.statement,
        image: cldUrl(publicId),
        roomPreviewImage: roomPub ? cldUrl(roomPub, 'f_auto,q_auto,w_1800') : null,
        roomPreviewAlt: roomPub
            ? (doc.roomPreviewImage?.alt || `${doc.title} mounted in a room`)
            : null,
        featured: !!doc.featured,
        startingAt: doc.startingPrice != null ? `$${doc.startingPrice}` : '',
        ...(doc.location ? {location: doc.location} : {}),
    };
};

// seriesFromCollection — parse the series name from the legacy `collection`
// display string used by data.js fallback prints. Both shapes are supported:
//
//   "Japan Series · Limited Edition" → "Japan Series"   (PRINTS shape)
//   "Architecture in Repose"          → "Architecture in Repose"  (TRAVEL_ARCHIVE shape)
//
// Returns null for missing/empty input. Consumer code on /atelier wraps
// this in a fallback chain: `print.series || seriesFromCollection(print.collection)`.
export const seriesFromCollection = (collection) => {
    if (!collection || typeof collection !== 'string') return null;
    const parts = collection.split(' · ');
    const head = (parts[0] || '').trim();
    return head || null;
};

// ─────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────

/**
 * Returns the site-wide settings constant. Shape is identical to
 * `SITE` from `data.js` so consumers can do `const SITE = useSiteSettings();`
 * and access the same keys (`SITE.name`, `SITE.email`, etc.).
 *
 * First render: `data.js` fallback. Subsequent render after Sanity
 * resolves: live data.
 */
export function useSiteSettings() {
    const [site, setSite] = useState(FALLBACK_SITE);

    useEffect(() => {
        let cancelled = false;
        fetchWithFallback(
            `*[_id == "siteSettings"][0]`,
            {},
            null,
        ).then((doc) => {
            if (cancelled) return;
            const mapped = mapSiteSettingsToSite(doc);
            // Only swap state if the fetch actually returned a doc. If it
            // returned null (timeout / not-found), keep the fallback.
            if (mapped) setSite(mapped);
        }).catch((err) => {
            // Defensive — fetchWithFallback shouldn't throw, but if it does
            // (e.g. dev-time module-resolution error), log and stay on
            // fallback so the page still renders.
            // eslint-disable-next-line no-console
            console.debug('[content] useSiteSettings fallback engaged:', err?.message);
        });
        return () => { cancelled = true; };
    }, []);

    return site;
}

/**
 * Returns the testimonials array (FE shape: id/quote/name/type/backdropImage)
 * for /reviews and /business. Filters to published docs tagged with the
 * given `tag`, ordered by `order` ascending.
 *
 * Phase 9 — parameterized so /business can call useTestimonials('business')
 * to get business-tagged docs without duplicating the hook. Backward-
 * compatible: default tag is 'reviews' so existing /reviews callers
 * keep working without modification.
 *
 * Falls back to data.js REVIEWS if Sanity is unreachable. The fallback is
 * not tag-aware (data.js has no tagging), so business-tag callers get the
 * same generic fallback as reviews — which is fine for a single hard-edge
 * unavailability case.
 */
export function useTestimonials(tag = 'reviews') {
    const [reviews, setReviews] = useState(FALLBACK_REVIEWS);

    useEffect(() => {
        let cancelled = false;
        fetchWithFallback(
            `*[_type == "testimonial" && published == true && $tag in tags] | order(order asc, _createdAt asc) {
                _id, quote, attribution, sessionType, context, order, featured,
                "backdropImage": backdropImage
            }`,
            {tag},
            null,
        ).then((docs) => {
            if (cancelled) return;
            if (Array.isArray(docs) && docs.length > 0) {
                const mapped = docs.map(mapTestimonialToFE).filter(Boolean);
                if (mapped.length > 0) setReviews(mapped);
            } else if (Array.isArray(docs) && docs.length === 0) {
                // Tag-scoped query returned zero docs (e.g. no testimonials
                // tagged 'business' yet). Surface an empty array so the
                // caller can hide the section cleanly instead of showing
                // stale fallback data from a different tag.
                setReviews([]);
            }
        }).catch((err) => {
            // eslint-disable-next-line no-console
            console.debug('[content] useTestimonials fallback engaged:', err?.message);
        });
        return () => { cancelled = true; };
    }, [tag]);

    return reviews;
}

/**
 * Returns the 8 shop-side fine-art prints (no `location` field — scope
 * discriminator per Phase 4 sign-off). Ordered by `order` asc with a stable
 * tiebreaker on `_id` so PR1..PR8 keeps a deterministic sequence on first
 * paint. Falls back to data.js PRINTS.
 */
export function useFineArtPrints() {
    const [prints, setPrints] = useState(FALLBACK_PRINTS);

    useEffect(() => {
        let cancelled = false;
        fetchWithFallback(
            `*[_type == "print" && published == true && !defined(location)] | order(order asc, _id asc) {
                _id, title, series, order, featured, editionType, editionSize, startingPrice, statement,
                "coverImage": coverImage,
                "roomPreviewImage": roomPreviewImage
            }`,
            {},
            null,
        ).then((docs) => {
            if (cancelled) return;
            if (Array.isArray(docs) && docs.length > 0) {
                const mapped = docs.map(mapPrintToFE).filter(Boolean);
                if (mapped.length > 0) setPrints(mapped);
            }
        }).catch((err) => {
            // eslint-disable-next-line no-console
            console.debug('[content] useFineArtPrints fallback engaged:', err?.message);
        });
        return () => { cancelled = true; };
    }, []);

    return prints;
}

/**
 * Returns the 6 travel-archive prints (`location` present — scope discriminator
 * per Phase 4 sign-off). Same fallback pattern.
 */
export function useTravelArchive() {
    const [archive, setArchive] = useState(FALLBACK_TRAVEL_ARCHIVE);

    useEffect(() => {
        let cancelled = false;
        fetchWithFallback(
            `*[_type == "print" && published == true && defined(location)] | order(order asc, _id asc) {
                _id, title, series, order, featured, location, editionType, editionSize, startingPrice, statement,
                "coverImage": coverImage,
                "roomPreviewImage": roomPreviewImage
            }`,
            {},
            null,
        ).then((docs) => {
            if (cancelled) return;
            if (Array.isArray(docs) && docs.length > 0) {
                const mapped = docs.map(mapPrintToFE).filter(Boolean);
                if (mapped.length > 0) setArchive(mapped);
            }
        }).catch((err) => {
            // eslint-disable-next-line no-console
            console.debug('[content] useTravelArchive fallback engaged:', err?.message);
        });
        return () => { cancelled = true; };
    }, []);

    return archive;
}

/**
 * Returns a single print (FE shape) looked up by FE id (e.g. "pr1", "ta3").
 * Sanity doc id convention from Phase 4 migration: `print-<feId>`.
 *
 * First render returns the data.js fallback synchronously so PrintDetail's
 * existing `if (!print) return <Navigate />` redirect still works on bad
 * ids. On Sanity success, swaps in the live doc. On Sanity error, keeps
 * the fallback — never falls back to `null` when data.js has the print.
 */
export function usePrint(id) {
    const synchronousFallback = FALLBACK_PRINTS.find((p) => p.id === id) || null;
    const [print, setPrint] = useState(synchronousFallback);

    useEffect(() => {
        if (!id) return undefined;
        let cancelled = false;
        fetchWithFallback(
            `*[_id == $sanityId][0] {
                _id, title, series, order, featured, location, editionType, editionSize, startingPrice, statement,
                "coverImage": coverImage,
                "roomPreviewImage": roomPreviewImage
            }`,
            {sanityId: `print-${id}`},
            null,
        ).then((doc) => {
            if (cancelled) return;
            const mapped = mapPrintToFE(doc);
            if (mapped) setPrint(mapped);
            // If Sanity returned null AND fallback was also null, leave state at null
            // (component already renders <Navigate to="/atelier" replace />).
        }).catch((err) => {
            // eslint-disable-next-line no-console
            console.debug('[content] usePrint fallback engaged:', err?.message);
        });
        return () => { cancelled = true; };
    }, [id]);

    return print;
}

/**
 * Returns a gallery's items array (FE shape — matches data.js PORTFOLIO
 * item shape) for the given slug. Falls back to the provided fallback
 * value (an array of items) if Sanity is unreachable.
 *
 * Dedup: concurrent calls from multiple components for the same slug share
 * one network round trip via `_galleryCache`. Per-session memoization
 * means re-visits to the same route also skip the network entirely.
 */
export function useGallery(slug, fallback) {
    const [items, setItems] = useState(fallback);

    useEffect(() => {
        if (!slug) return undefined;
        let cancelled = false;
        fetchGalleryBySlug(slug).then((rawItems) => {
            if (cancelled) return;
            if (Array.isArray(rawItems)) {
                const mapped = rawItems.map(mapGalleryItemToFE).filter(Boolean);
                if (mapped.length > 0) setItems(mapped);
            }
        }).catch((err) => {
            // eslint-disable-next-line no-console
            console.debug(`[content] useGallery(${slug}) fallback engaged:`, err?.message);
        });
        return () => { cancelled = true; };
    }, [slug]);

    return items;
}

/**
 * Convenience hook for the unified portfolio gallery (slug
 * 'portfolio-all'). Used by /portfolio, /life-legacy, /brand-business —
 * all three share one underlying gallery doc and filter client-side by
 * lifeTags / brandTags / category.
 */
export function usePortfolio() {
    return useGallery('portfolio-all', FALLBACK_PORTFOLIO);
}

/**
 * Convenience hook for the curated Business landing-page gallery
 * (slug 'business-selected-work'). 17 items, ordered as authored in
 * Sanity. Falls back to BUSINESS_GALLERY_ITEMS in data.js (the same
 * 17 items, same order — moved out of BusinessArizona.jsx in Phase 5.4).
 */
export function useBusinessSelectedWork() {
    return useGallery('business-selected-work', FALLBACK_BUSINESS_GALLERY);
}

/**
 * Phase 14 — Convenience hook for the clinic page's own editable gallery
 * (slug 'clinic-selected-work'). Seeded by /app/sanity-studio/scripts/
 * seed-phase15-galleries.mjs from the same items as business-selected-
 * work, then curated independently by Eli in Studio.
 *
 * Fallback: until the Sanity doc exists (or while it loads), serve the
 * same FALLBACK_BUSINESS_GALLERY constant that /business uses — so the
 * /aesthetic-clinic-photography page never renders empty even if the
 * seed script hasn't been run yet. Once the doc is created and curated,
 * Sanity wins and the page diverges from /business.
 *
 * Editing this doc in Studio changes ONLY the clinic page; /business
 * continues to read its own slug above and is unaffected.
 */
export function useClinicSelectedWork() {
    return useGallery('clinic-selected-work', FALLBACK_BUSINESS_GALLERY);
}

/**
 * Home-Services campaign landing page (`/home-services-photography`) reads
 * its own editable Sanity gallery (slug 'home-services-selected-work').
 * The Sanity doc is seeded from `business-selected-work` (deep copy with
 * new keys) by /app/sanity-studio/scripts/seed-phase15-galleries.mjs so
 * Eli can swap to trades-appropriate photos in Studio without affecting
 * /business.
 *
 * Fallback: FALLBACK_HOME_SERVICES_GALLERY — the same 17 business items
 * with `id` rekeyed `hs1..hs17`. Page never renders empty even before
 * the seed script runs. Once the doc exists and is curated, Sanity wins
 * and the page diverges from /business independently.
 */
export function useHomeServicesSelectedWork() {
    return useGallery('home-services-selected-work', FALLBACK_HOME_SERVICES_GALLERY);
}

export function useHomeServicesVideo() {
    return useGallery('home-services-video', []);
}

/**
 * Phase 15 — Per-page video gallery hooks.
 *
 * Each in-scope page renders a <VideoGallerySection> that reads its own
 * dedicated `*-video` gallery instance. The hooks below pass an EMPTY
 * fallback (`[]`) — unlike the photo hooks above which fall back to a
 * mock gallery to avoid empty states. For video, empty IS the empty state
 * (no fabricated/placeholder content per Eli's brief), and the
 * VideoGallerySection auto-hides the entire section when the array is
 * empty. So:
 *
 *   - Sanity doc doesn't exist yet   → hook returns []  → section hidden
 *   - Sanity doc exists, unpublished → hook returns []  → section hidden
 *                                      (GROQ filters by published==true)
 *   - Sanity doc published, empty    → hook returns []  → section hidden
 *   - Sanity doc published with N≥1  → hook returns [N] → section renders
 *
 * Editing each doc in Studio changes ONLY the corresponding page.
 */
export function useBusinessVideo() {
    return useGallery('business-video', []);
}
export function useLifeVideo() {
    return useGallery('life-video', []);
}
export function useClinicVideo() {
    return useGallery('clinic-video', []);
}
export function usePortfolioVideo() {
    return useGallery('portfolio-video', []);
}

// ─────────────────────────────────────────────────────────────────────────
// Hero media
// ─────────────────────────────────────────────────────────────────────────

// heroMedia (Sanity) → FE shape `{desktop, mobile, srcSet, alt}`.
// FE pages consume hero images via <img src srcSet sizes alt /> directly.
// Build the responsive URL variants from publicId at adapter time so the
// FE doesn't need to know about Cloudinary transforms.
const HERO_WIDTHS_DESKTOP = [900, 1400, 2000, 2400, 3000];
const buildHeroSrcSet = (publicId) => publicId
    ? HERO_WIDTHS_DESKTOP.map((w) => `${cldUrl(publicId, `f_auto,q_auto,w_${w}`)} ${w}w`).join(', ')
    : null;

const mapHeroMediaToFE = (doc) => {
    if (!doc) return null;
    const desktopPub = doc.desktop?.image?.public_id;
    const mobilePub = doc.mobile?.image?.public_id;
    if (!desktopPub) return null;
    return {
        desktop: cldUrl(desktopPub, 'f_auto,q_auto,w_2400'),
        mobile: mobilePub ? cldUrl(mobilePub, 'f_auto,q_auto,w_1200') : null,
        srcSet: buildHeroSrcSet(desktopPub),
        alt: doc.desktop?.alt || doc.title || 'Photography by Elizaveta von Stuben Imagery',
    };
};

// Module-level cache for heroMedia (parallel to _galleryCache). Same
// stale-while-revalidate + null-eviction pattern.
const _heroMediaCache = new Map();
const fetchHeroMediaBySlug = (slug) => {
    if (_heroMediaCache.has(slug)) return _heroMediaCache.get(slug);
    const p = fetchWithFallback(
        `*[_type == "heroMedia" && slug.current == $slug && published == true][0] {
            title, mediaType, desktop, mobile
        }`,
        {slug},
        null,
    );
    _heroMediaCache.set(slug, p);
    p.then((v) => { if (v == null) _heroMediaCache.delete(slug); });
    return p;
};

/**
 * Returns hero media for the given slug. Each slug maps to one heroMedia
 * doc in Sanity (e.g. 'home-hero', 'about-image', 'life-legacy-hero',
 * 'brand-business-hero'). Caller must provide a fallback shape so the
 * page renders synchronously on first paint.
 *
 * Fallback shape: { desktop: <url string>, mobile: <url string or null>,
 *                   srcSet: <srcSet string or null>, alt: <string> }
 */
export function useHeroMedia(slug, fallback) {
    const [hero, setHero] = useState(fallback);

    useEffect(() => {
        if (!slug) return undefined;
        let cancelled = false;
        fetchHeroMediaBySlug(slug).then((doc) => {
            if (cancelled) return;
            const mapped = mapHeroMediaToFE(doc);
            if (mapped) setHero(mapped);
        }).catch((err) => {
            // eslint-disable-next-line no-console
            console.debug(`[content] useHeroMedia(${slug}) fallback engaged:`, err?.message);
        });
        return () => { cancelled = true; };
    }, [slug]);

    return hero;
}

/**
 * Convenience hooks for each canonical hero slug. Fallbacks are wired to
 * the existing data.js URL exports so first-paint shows the same image
 * the site has always shown if Sanity is unreachable.
 */
export function useHomeHero() {
    return useHeroMedia('home-hero', {
        desktop: FALLBACK_HERO_IMAGE,
        mobile: FALLBACK_HERO_IMAGE_MOBILE,
        srcSet: FALLBACK_HERO_IMAGE_SRCSET,
        alt: 'Refined photography by Elizaveta von Stuben Imagery — visual storytelling for life, family, brands and business in Scottsdale, Phoenix & New York.',
    });
}

export function useAboutImage() {
    return useHeroMedia('about-image', {
        desktop: FALLBACK_ABOUT_IMAGE,
        mobile: null,
        srcSet: null,
        alt: 'Elizaveta von Stubendorf',
    });
}

/**
 * Phase 8 — /atelier hero. Wall-mockup image authored by the user as a
 * `heroMedia` doc with slug `atelier-hero`. Has no data.js fallback by
 * design: when the user hasn't yet uploaded the hero in Studio (the
 * Phase 8 transition window), this hook returns `null` and `Prints.jsx`
 * hides the hero section entirely. Once authored, the next page load
 * picks it up via the standard stale-while-revalidate fetch.
 */
export function useAtelierHero() {
    return useHeroMedia('atelier-hero', null);
}

/**
 * Phase 17 — Wires /aesthetic-clinic-photography hero to Sanity. The
 * doc with slug `aesthetic-clinic-hero` (heroMedia type) drives both
 * image and video heroes. When missing/unpublished, returns null and
 * the page falls back to <HeroVideo>'s built-in business placeholder
 * — so the page never breaks while Eli is still authoring the doc.
 */
export function useClinicHero() {
    return useHeroMedia('aesthetic-clinic-hero', null);
}

/**
 * Home-Services campaign landing page hero. Authored as a `heroMedia` doc
 * with slug `home-services-hero`. When missing/unpublished, returns null
 * and the page falls back to <HeroVideo>'s built-in business placeholder
 * — same pattern as useClinicHero. Eli can swap to trades footage in
 * Studio with no FE deploy.
 */
export function useHomeServicesHero() {
    return useHeroMedia('home-services-hero', null);
}

// ─────────────────────────────────────────────────────────────────────────
// About Elizaveta — singleton carousel
// ─────────────────────────────────────────────────────────────────────────
//
// One site-wide document (`_id == "aboutElizaveta"`) holding the media
// (photos + videos of Elizaveta) shown on every service page's "Hi, I'm
// Elizaveta" section. The text is hardcoded per page; only the media is
// editorial.
//
// Output shape per slide (consumer can render image OR video uniformly):
//   {
//     id, mediaType: 'image' | 'video',
//     image: <url>,      // poster URL for video; full URL for image
//     videoSrc: <url>,   // present only when mediaType === 'video'
//     alt: <string>,
//     ratio: <number|null>, naturalWidth, naturalHeight,
//   }
//
// Empty array when:
//   - doc doesn't exist yet
//   - doc exists but published === false
//   - doc exists, published, but slides[] is empty
// The FE section handles all three identically (carousel column drops,
// section still renders as text + CTA only — Phase 18 5b).

const mapAboutSlideToFE = (item) => {
    if (!item) return null;
    // Per Sanity-convention parity with galleries: the cloudinaryAsset's
    // `image` field is the source of truth. Photos and videos both come
    // through it; the FE detects via `image.resource_type`. No separate
    // `mediaType` / `video` / `poster` projection — same as galleries.
    const publicId = item.image?.public_id;
    if (!publicId) return null;
    const isVideo = item.image?.resource_type === 'video';
    const naturalRatio =
        item.image?.width && item.image?.height
            ? item.image.width / item.image.height
            : null;
    const alt = item.alt || item.caption || 'Elizaveta von Stuben at work';
    const common = {
        id: item._key || publicId,
        alt,
        caption: item.caption || null,
        ratio: naturalRatio,
        naturalWidth: item.image?.width || null,
        naturalHeight: item.image?.height || null,
    };
    if (isVideo) {
        // Always auto-derive the poster from the source video (frame at
        // offset 0 via Cloudinary's so_0 transform). Eli can still pick a
        // poster override through the cloudinaryAsset object — that field
        // lives on the underlying object and is hidden in Studio unless
        // the picked asset is a video — but the singleton's GROQ only
        // asks for { image, alt, caption } per spec, so we don't read it
        // here. Auto-derive is the same default the galleries use when no
        // override is set.
        return {
            ...common,
            mediaType: 'video',
            videoSrc: cldVideoUrl(publicId),
            image: cldPosterUrl(publicId, 'so_0,f_auto,q_auto,w_1400,c_limit'),
        };
    }
    return {
        ...common,
        mediaType: 'image',
        image: cldUrl(publicId, 'f_auto,q_auto,w_1400'),
        srcSet: [600, 900, 1200, 1600]
            .map((w) => `${cldUrl(publicId, `f_auto,q_auto,w_${w}`)} ${w}w`)
            .join(', '),
    };
};

const _aboutElizavetaCache = {value: undefined, promise: null};

const fetchAboutElizaveta = () => {
    if (_aboutElizavetaCache.promise) return _aboutElizavetaCache.promise;
    // GROQ projection matches the singleton spec exactly:
    //   { slides[]{ image, alt, caption } }
    // No `_key` projected separately — Sanity includes it on array
    // items automatically when we ask for the object as-is, but to be
    // safe we project it explicitly so React keys stay stable.
    const p = fetchWithFallback(
        `*[_type == "aboutElizaveta" && published == true][0] {
            slides[] {
                _key,
                image,
                alt,
                caption
            }
        }`,
        {},
        null,
    );
    _aboutElizavetaCache.promise = p;
    p.then((v) => {
        if (v == null) _aboutElizavetaCache.promise = null;
    });
    return p;
};

/**
 * Returns the mapped slides array for the About Elizaveta carousel.
 * Always returns an array (possibly empty). The consumer renders the
 * section as text + CTA only when the array is empty.
 */
export function useAboutElizavetaSlides() {
    const [slides, setSlides] = useState([]);

    useEffect(() => {
        let cancelled = false;
        fetchAboutElizaveta().then((doc) => {
            if (cancelled) return;
            if (doc && Array.isArray(doc.slides)) {
                const mapped = doc.slides.map(mapAboutSlideToFE).filter(Boolean);
                setSlides(mapped);
            }
        }).catch((err) => {
            // eslint-disable-next-line no-console
            console.debug('[content] useAboutElizavetaSlides fallback engaged:', err?.message);
        });
        return () => { cancelled = true; };
    }, []);

    return slides;
}
